/* eslint-disable @typescript-eslint/no-dynamic-delete */
import type { Identifiable } from '~/models/identifiable'
import type { LocalStateChange } from './stateChange'
import {
  LocalStateAdd,
  LocalStateRemove,
  LocalStateUpdate,
} from './stateChange'

export class LocalChanges<Item extends Identifiable> {
  private _added: Record<string, LocalStateAdd<Item>> = {}
  private _removed: Record<string, LocalStateRemove<Item>> = {}
  private _updated: Record<string, LocalStateUpdate<Item>> = {}

  private _addedDepths: Set<number> = new Set()
  private _removedDepths: Set<number> = new Set()
  private _updatedDepths: Set<number> = new Set()

  private getDepths(
    collection: Record<string, LocalStateChange<Item>>,
  ): Set<number> {
    const depths = new Set<number>()

    for (const change of Object.values(collection)) {
      depths.add(change.depth)
    }

    return depths
  }

  getStateUpdate(newStateItem: Item) {
    return this.getChange(this._updated, this._updatedDepths, newStateItem)
  }

  getStateRemove(newStateItem: Item) {
    return this.getChange(this._removed, this._removedDepths, newStateItem)
  }

  getStateAdd(newStateItem: Item) {
    return this.getChange(this._added, this._addedDepths, newStateItem)
  }

  getStateAddEntities(): LocalStateAdd<Item>[] {
    return Object.values(this._added)
  }

  addStateChange(change: LocalStateChange<Item>) {
    if (change instanceof LocalStateAdd) {
      this._added[change.id] = change
      this._addedDepths.add(change.depth || 0)
    }
    else if (change instanceof LocalStateRemove) {
      this._removed[change.id] = change
      this._removedDepths.add(change.depth || 0)
    }
    else if (change instanceof LocalStateUpdate) {
      this._updated[change.id] = change
      this._updatedDepths.add(change.depth || 0)
    }
  }

  removeStateChange(change: LocalStateChange<Item>) {
    if (change instanceof LocalStateAdd) {
      delete this._added[change.id]
      this._addedDepths = this.getDepths(this._added)
    }
    else if (change instanceof LocalStateRemove) {
      delete this._removed[change.id]
      this._removedDepths = this.getDepths(this._removed)
    }
    else if (change instanceof LocalStateUpdate) {
      delete this._updated[change.id]
      this._updatedDepths = this.getDepths(this._updated)
    }
  }

  private getChange<T>(
    collection: Record<string, T>,
    depths: Set<number>,
    item: Item,
  ): T | undefined {
    for (const depth of depths) {
      const id = this.getId(item, depth)

      if (!id) {
        continue
      }
      const changeItem = collection[id]
      if (changeItem) {
        return changeItem
      }
    }

    return undefined
  }

  private getId(item: Item, depth: number): string | undefined {
    if (depth === 0) {
      return item.id
    }

    let current: any = item.ref
    for (let i = 0; i < depth; i++) {
      current = current.parent
      if (!current) {
        return undefined
      }
    }
    return current?.id
  }

  public hasChanges(): boolean {
    return (
      this.hasUpdateChanges() || this.hasAddChanges() || this.hasRemoveChanges()
    )
  }

  public hasUpdateChanges(): boolean {
    return Object.keys(this._updated).length > 0
  }

  public hasAddChanges(): boolean {
    return Object.keys(this._added).length > 0
  }

  public hasRemoveChanges(): boolean {
    return Object.keys(this._removed).length > 0
  }

  public removeInactiveStateChanges(
    activeStateChanges: LocalStateRemove<Item>[],
  ) {
    const idsToKeep = new Set<string>()
    for (const change of activeStateChanges) {
      idsToKeep.add(change.id)
    }

    const idsToRemove = new Set<string>(Object.keys(this._removed))
    for (const id of idsToKeep) {
      idsToRemove.delete(id)
    }

    for (const id of idsToRemove) {
      delete this._removed[id]
    }

    this._removedDepths = this.getDepths(this._removed)
  }
}
